import { useEffect, useState } from 'react';
import { FileText, Calendar, AlertCircle } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import axios from 'axios';

// Configuration de PDF.js worker
// Utilisation du CDN pour le worker (plus simple avec Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface DocumentLegal {
  id: number;
  type_document: string;
  titre: string;
  fichier_pdf: string;
  date_mise_a_jour: string;
  url_pdf?: string;
}

export default function CGUPage() {
  const [document, setDocument] = useState<DocumentLegal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState<number | null>(null);

  useEffect(() => {
    const fetchCGU = async () => {
      try {
        setLoading(true);
        const api = axios.create({
          baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api',
        });
        const response = await api.get('/documents-legaux/cgu/');
        setDocument(response.data);
        setError('');
      } catch (err: any) {
        console.error('Erreur lors du chargement des CGU:', err);
        setError('Impossible de charger les Conditions Générales d\'Utilisation.');
      } finally {
        setLoading(false);
      }
    };

    fetchCGU();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des CGU...</p>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Conditions générales d'utilisation
          </h1>
          <p className="text-gray-600 mb-6">
            {error || 'Les Conditions Générales d\'Utilisation ne sont pas disponibles pour le moment.'}
          </p>
          <a
            href="/contact"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Nous contacter
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* En-tête */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <div className="flex items-center mb-4">
              <FileText className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">
                {document.titre}
              </h1>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>Dernière modification le {new Date(document.date_mise_a_jour).toLocaleDateString('fr-FR', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
            </div>
          </div>

          {/* Contenu PDF */}
          <div className="mb-8">
            {document.url_pdf && (
              <div className="mt-6">
                <Document
                  file={document.url_pdf}
                  options={{
                    cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
                    cMapPacked: true,
                  }}
                  onLoadSuccess={({ numPages }) => {
                    setNumPages(numPages);
                    setError('');
                  }}
                  onLoadError={(error) => {
                    console.error('Erreur lors du chargement du PDF:', error);
                    console.error('URL du PDF:', document.url_pdf);
                    setError(`Impossible de charger le PDF: ${error.message || 'Erreur inconnue'}`);
                  }}
                  loading={
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      <span className="ml-4 text-gray-600">Chargement du PDF...</span>
                    </div>
                  }
                  className="flex flex-col items-center"
                >
                  {numPages && Array.from({ length: numPages }, (_, index) => (
                    <div key={`page-${index + 1}`} className="mb-4 flex justify-center">
                      <Page
                        pageNumber={index + 1}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        className="shadow-lg"
                        width={Math.min(800, typeof window !== 'undefined' ? window.innerWidth - 100 : 800)}
                        loading={
                          <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          </div>
                        }
                      />
                    </div>
                  ))}
                </Document>
              </div>
            )}
          </div>

          {/* Informations supplémentaires */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <p className="text-sm text-gray-600 text-center">
              Pour toute question concernant ces Conditions Générales d'Utilisation, 
              veuillez{' '}
              <a href="/contact" className="text-blue-600 hover:text-blue-700 font-medium">
                nous contacter
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

